import React from "react"
import { useRouter } from "next/router"

import { useRevalidateMetricsCatalog } from "@/metrics/catalog"
import { useMetricRecords } from "@/metrics/registry"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@coval/ui"

import { PickerCreateButton } from "@/components/entity-picker/PickerCreateButton"
import { useQueryParams } from "@/hooks/useQueryParams"

import { MetricTable, type MetricTableProps } from "./MetricTable"

interface MetricModalProps extends MetricTableProps {
  isOpen: boolean
  onClose: () => void
  onApply: () => void
  createHref?: string
  createLabel?: string
  /**
   * Rendered above the table, for callers that must warn about where the run goes.
   * It carries its own bottom spacing, so one that renders nothing takes no room.
   */
  banner?: React.ReactNode
}

// Owns the catalog subscription so it is only established when the button is
// actually rendered — callers without an organization slug never reach for the
// transport layer just to render the picker.
function MetricCreateButton({ href, label }: { href: string; label: string }) {
  // The metrics page opens in its own tab, whose query cache is not this one,
  // so the invalidation it fires on success never reaches this catalog.
  const revalidateMetrics = useRevalidateMetricsCatalog()

  return (
    <PickerCreateButton
      label={label}
      href={href}
      onReturn={revalidateMetrics}
      testId="metric-create-button"
    />
  )
}

export function MetricModal({
  className,
  isOpen,
  onClose,
  onApply,
  metrics,
  selectedMetrics,
  selectionCount,
  onToggleMetric,
  agentMetricsIds,
  instanceId,
  allowMultipleSelection = true,
  maxSelections,
  metricErrors = {},
  tagColorMap,
  banner,
  createHref,
  createLabel,
}: MetricModalProps) {
  const router = useRouter()
  const queryParams = useQueryParams()
  const orgSlug =
    typeof router.query.slug === "string" ? router.query.slug : undefined
  // A metric takes its type before there is a form to author, which is what
  // the metrics page's `create=true` overlay asks for.
  const defaultCreateHref = orgSlug
    ? queryParams.getHref("/metrics?create=true", orgSlug)
    : null
  const resolvedCreateHref = createHref ?? defaultCreateHref
  const { metricRecords } = useMetricRecords()
  const resolvedSelectionCount = selectionCount ?? selectedMetrics.length
  const resolvedMetrics =
    metrics && metrics.length > 0 ? metrics : Object.values(metricRecords || {})
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-6xl" data-testid="metrics-dialog">
        {/* The table owns the body's horizontal gutter (its section platters run
            edge to edge inside it), so the body itself adds none. The title goes
            down into the table's controls row rather than into a header of its
            own, so the search sits beside the heading instead of in a band under
            it. */}
        <DialogBody className="overflow-hidden p-0">
          <MetricTable
            title={
              <DialogTitle className="text-xl">
                {resolvedSelectionCount > 0
                  ? `Select Metrics (${resolvedSelectionCount})`
                  : "Select Metrics"}
              </DialogTitle>
            }
            banner={banner}
            className={className ?? "h-[70vh]"}
            metrics={resolvedMetrics}
            selectedMetrics={selectedMetrics}
            selectionCount={selectionCount}
            agentMetricsIds={agentMetricsIds}
            onToggleMetric={onToggleMetric}
            instanceId={instanceId}
            allowMultipleSelection={allowMultipleSelection}
            maxSelections={maxSelections}
            metricErrors={metricErrors}
            tagColorMap={tagColorMap}
          />
        </DialogBody>
        <DialogFooter className="border-t border-coval-border px-6 py-5">
          {resolvedCreateHref ? (
            <div className="mr-auto">
              <MetricCreateButton
                href={resolvedCreateHref}
                label={createLabel ?? "Create Metric"}
              />
            </div>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={
              maxSelections !== undefined &&
              resolvedSelectionCount > maxSelections
            }
            data-testid="metrics-apply-button"
          >
            Apply Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
